import React, { useState, useEffect, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatAUD, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Search, Users, Package, Store, Printer, FileText, FileSpreadsheet,
  LayoutList, Smartphone, Plus, Minus, Ban, StickyNote, Share2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = ["BELUM DIBELI", "DIBELI SEBAGIAN", "PEMBELIAN LENGKAP", "KELEBIHAN PEMBELIAN", "TIDAK DITEMUKAN", "DIGANTI PRODUK LAIN", "DIBATALKAN"];
const OVERRIDE_OPTIONS = ["TIDAK DITEMUKAN", "DIGANTI PRODUK LAIN", "DIBATALKAN"];
const ALLOC_METHODS = [
  { v: "oldest_first", l: "Order Terlama Dulu" },
  { v: "fully_paid_first", l: "Pelanggan Lunas Dulu" },
  { v: "deposit_paid_first", l: "Pelanggan DP Dulu" },
  { v: "manual", l: "Manual" },
];

function Stat({ label, value, tone }) {
  const c = tone === "danger" ? "text-[hsl(var(--danger))]" : tone === "success" ? "text-[hsl(var(--success))]" : "";
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`font-heading text-lg font-semibold ${c}`}>{value}</p>
    </div>
  );
}

export default function ShoppingList() {
  const { selectedTripId, trips } = useApp();
  const { isAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fStore, setFStore] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fFlag, setFFlag] = useState("");
  const [view, setView] = useState("store"); // store | mobile

  // dialogs
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [allocItem, setAllocItem] = useState(null);
  const [statusItem, setStatusItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/shopping-list", {
        params: {
          trip_id: selectedTripId,
          search: search || undefined,
          store: fStore || undefined,
          status: fStatus || undefined,
          category: fCategory || undefined,
          flag: fFlag || undefined,
        },
      });
      setData(r.data);
    } catch { toast.error("Gagal memuat daftar belanja"); }
    finally { setLoading(false); }
  }, [selectedTripId, search, fStore, fStatus, fCategory, fFlag]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const doExport = async (fmt) => {
    try {
      const token = localStorage.getItem("jastip_token");
      const params = new URLSearchParams({ trip_id: selectedTripId || "all", format: fmt });
      if (search) params.set("search", search);
      if (fStore) params.set("store", fStore);
      if (fStatus) params.set("status", fStatus);
      if (fCategory) params.set("category", fCategory);
      if (fFlag) params.set("flag", fFlag);
      const res = await fetch(`${API}/shopping-list/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daftar_belanja.${fmt === "xlsx" ? "xlsx" : fmt === "pdf" ? "pdf" : "csv"}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Unduhan dimulai");
    } catch { toast.error("Gagal mengekspor"); }
  };

  const shareList = () => {
    if (!data) return;
    const lines = (data.groups || []).map((g) => {
      const its = g.items.map((i) => `- ${i.name}${i.variant ? ` (${i.variant})` : ""}: sisa ${i.remaining_to_purchase} (pesan ${i.total_ordered_quantity}, beli ${i.total_purchased_quantity})`).join("\n");
      return `*${g.store}*\n${its}`;
    }).join("\n\n");
    const msg = `Daftar Belanja\n\n${lines}`;
    if (navigator.share) navigator.share({ title: "Daftar Belanja", text: msg }).catch(() => {});
    else window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const s = data?.summary;

  return (
    <div>
      <PageHeader title="Daftar Belanja" subtitle="Rencana belanja gabungan dari semua order aktif">
        <div className="flex items-center gap-2 flex-wrap no-print">
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button onClick={() => setView("store")} className={`px-3 h-9 flex items-center gap-1 text-sm ${view === "store" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="sl-view-list"><LayoutList className="h-4 w-4" /></button>
            <button onClick={() => setView("mobile")} className={`px-3 h-9 flex items-center gap-1 text-sm ${view === "mobile" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="sl-view-mobile"><Smartphone className="h-4 w-4" /></button>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="sl-print"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
          <Button variant="outline" size="sm" onClick={() => doExport("pdf")} data-testid="sl-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => doExport("xlsx")} data-testid="sl-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => doExport("csv")} data-testid="sl-export-csv">CSV</Button>
          <Button variant="outline" size="sm" onClick={shareList} data-testid="sl-share"><Share2 className="h-4 w-4 mr-1.5" />Bagikan</Button>
        </div>
      </PageHeader>

      {/* Summary */}
      {s && (
        <div className="space-y-4 mb-5 no-print" data-testid="sl-summary">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <Stat label="Produk Unik" value={s.total_unique_products} />
            <Stat label="Unit Dipesan" value={s.total_ordered} />
            <Stat label="Unit Dibeli" value={s.total_purchased} tone="success" />
            <Stat label="Sisa Beli" value={s.total_remaining} tone="danger" />
            <Stat label="Lengkap" value={s.total_completed} tone="success" />
            <Stat label="Kurang / Tidak Ada" value={`${s.total_shortages} / ${s.total_not_found}`} />
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Pembelian selesai {s.progress_percent}%</p>
              <p className="text-xs text-muted-foreground">Estimasi {formatAUD(s.estimated_cost_aud)} · Aktual {formatAUD(s.actual_cost_aud)}</p>
            </div>
            <Progress value={s.progress_percent} data-testid="sl-progress" />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-4 no-print">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari produk / brand..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sl-search" />
        </div>
        <SearchableSelect
          value={fStore || "all"}
          onChange={(v) => setFStore(v === "all" ? "" : v)}
          options={[{ value: "all", label: "Semua Toko", keywords: ["semua"] },
            { value: "__none__", label: "Toko Belum Ditentukan" },
            ...((data?.stores || []).map((st) => ({ value: st, label: st })))]}
          placeholder="Toko"
          searchPlaceholder="Cari toko..."
          emptyText="Toko tidak ditemukan"
          testId="sl-filter-store"
        />
        <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="sl-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {STATUS_OPTIONS.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchableSelect
          value={fCategory || "all"}
          onChange={(v) => setFCategory(v === "all" ? "" : v)}
          options={[{ value: "all", label: "Semua Kategori", keywords: ["semua"] },
            ...((data?.categories || []).map((c) => ({ value: c, label: c })))]}
          placeholder="Kategori"
          searchPlaceholder="Cari kategori..."
          emptyText="Kategori tidak ditemukan"
          testId="sl-filter-category"
        />
        <Select value={fFlag || "all"} onValueChange={(v) => setFFlag(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="sl-filter-flag"><SelectValue placeholder="Filter Cepat" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="shortage">Perlu Dibeli (Shortage)</SelectItem>
            <SelectItem value="completed">Pembelian Lengkap</SelectItem>
            <SelectItem value="not_found">Tidak Ditemukan</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loading /> : !data || data.items.length === 0 ? (
        <EmptyState title="Belum ada item belanja" subtitle="Item muncul otomatis dari order aktif pelanggan." />
      ) : view === "mobile" ? (
        <MobileMode groups={data.groups} onPurchase={setPurchaseItem} onAlloc={setAllocItem} onStatus={setStatusItem} isAdmin={isAdmin} />
      ) : (
        <div className="space-y-6">
          {data.groups.map((g) => (
            <div key={g.store} data-testid="sl-store-group">
              <div className="flex items-center gap-2 mb-2">
                <Store className="h-4 w-4 text-primary" />
                <h3 className="font-heading font-semibold">{g.store}</h3>
                <span className="text-xs text-muted-foreground">({g.items.length} produk)</span>
              </div>
              <div className="grid gap-3">
                {g.items.map((it) => (
                  <ItemRow key={it.product_id} it={it} isAdmin={isAdmin}
                    onPurchase={() => setPurchaseItem(it)} onAlloc={() => setAllocItem(it)} onStatus={() => setStatusItem(it)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Print-only simplified list */}
      {data && (
        <div className="print-only">
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>Daftar Belanja</h1>
          {data.groups.map((g) => (
            <div key={g.store} style={{ marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, marginBottom: 6 }}>{g.store}</h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Produk", "Varian", "Dipesan", "Dibeli", "Sisa", "Catatan"].map((h) => (
                      <th key={h} style={{ border: "1px solid #cbd5e1", padding: 4, textAlign: "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((i) => (
                    <tr key={i.product_id}>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.name}</td>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.variant}</td>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.total_ordered_quantity}</td>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.total_purchased_quantity}</td>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.remaining_to_purchase}</td>
                      <td style={{ border: "1px solid #cbd5e1", padding: 4 }}>{i.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {purchaseItem && (
        <PurchaseDialog item={purchaseItem} user={user} trips={trips}
          onClose={() => setPurchaseItem(null)} onSaved={() => { setPurchaseItem(null); load(); }} />
      )}
      {allocItem && (
        <AllocationDialog item={allocItem} isAdmin={isAdmin} defaultMethod={data?.allocation_method}
          onClose={() => setAllocItem(null)} onSaved={() => { setAllocItem(null); load(); }} />
      )}
      {statusItem && (
        <StatusDialog item={statusItem} onClose={() => setStatusItem(null)} onSaved={() => { setStatusItem(null); load(); }} />
      )}
    </div>
  );
}

function ItemRow({ it, isAdmin, onPurchase, onAlloc, onStatus }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 flex flex-col sm:flex-row gap-3" data-testid="sl-item">
      <div className="h-16 w-16 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
        {it.photo ? <img src={it.photo} alt={it.name} className="h-full w-full object-cover" /> : <Package className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <p className="font-medium">{it.name}</p>
            <p className="text-xs text-muted-foreground">{[it.brand, it.variant, it.trip_name].filter(Boolean).join(" · ")}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={it.purchase_status} />
            <span className="text-[11px] text-muted-foreground">{it.status_message}</span>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-2 text-center">
          <div><p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5"><Users className="h-3 w-3" />Cust</p><p className="font-semibold text-sm">{it.num_customers}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Pesan</p><p className="font-semibold text-sm">{it.total_ordered_quantity}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Beli</p><p className="font-semibold text-sm">{it.total_purchased_quantity}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Sisa</p><p className="font-semibold text-sm text-[hsl(var(--danger))]">{it.remaining_to_purchase}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Surplus</p><p className="font-semibold text-sm">{it.surplus_quantity}</p></div>
          <div><p className="text-[10px] text-muted-foreground">Est. AUD</p><p className="font-semibold text-xs">{formatAUD(it.estimated_aud_price)}</p></div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3 no-print">
          <Button size="sm" onClick={onPurchase} data-testid="sl-record-purchase"><Plus className="h-3.5 w-3.5 mr-1" />Catat Pembelian</Button>
          <Button size="sm" variant="outline" onClick={onAlloc} data-testid="sl-view-customers"><Users className="h-3.5 w-3.5 mr-1" />Lihat Customer</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={onStatus} data-testid="sl-set-status"><StickyNote className="h-3.5 w-3.5 mr-1" />Status / Catatan</Button>}
        </div>
      </div>
    </div>
  );
}

function MobileMode({ groups, onPurchase, onAlloc, onStatus, isAdmin }) {
  const items = groups.flatMap((g) => g.items);
  return (
    <div className="grid gap-4 sm:grid-cols-2 max-w-2xl mx-auto">
      {items.map((it) => {
        const pct = it.total_ordered_quantity > 0 ? Math.round((it.total_purchased_quantity / it.total_ordered_quantity) * 100) : 0;
        return (
          <div key={it.product_id} className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm" data-testid="sl-mobile-card">
            <div className="aspect-video bg-secondary flex items-center justify-center overflow-hidden">
              {it.photo ? <img src={it.photo} alt={it.name} className="h-full w-full object-cover" /> : <Package className="h-12 w-12 text-muted-foreground" />}
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-heading font-semibold text-lg leading-tight">{it.name}</p>
                <StatusBadge status={it.purchase_status} />
              </div>
              <p className="text-xs text-muted-foreground">{[it.brand, it.variant, it.store].filter(Boolean).join(" · ")}</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[11px] text-muted-foreground">Pesan</p><p className="text-2xl font-bold">{it.total_ordered_quantity}</p></div>
                <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[11px] text-muted-foreground">Beli</p><p className="text-2xl font-bold text-[hsl(var(--success))]">{it.total_purchased_quantity}</p></div>
                <div className="rounded-xl bg-secondary/50 py-2"><p className="text-[11px] text-muted-foreground">Sisa</p><p className="text-2xl font-bold text-[hsl(var(--danger))]">{it.remaining_to_purchase}</p></div>
              </div>
              <Progress value={pct} />
              <div className="grid grid-cols-2 gap-2">
                <Button className="h-11" onClick={() => onPurchase(it)} data-testid="sl-mobile-purchase"><Plus className="h-4 w-4 mr-1" />Catat Pembelian</Button>
                <Button className="h-11" variant="outline" onClick={() => onAlloc(it)}><Users className="h-4 w-4 mr-1" />Lihat Customer</Button>
                {isAdmin && <Button className="h-11" variant="outline" onClick={() => onStatus(it)}><Ban className="h-4 w-4 mr-1" />Tidak Ada</Button>}
                {isAdmin && <Button className="h-11" variant="outline" onClick={() => onStatus(it)}><StickyNote className="h-4 w-4 mr-1" />Catatan</Button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PurchaseDialog({ item, user, trips, onClose, onSaved }) {
  const [form, setForm] = useState({
    quantity: Math.max(item.remaining_to_purchase, 1),
    actual_aud_price: item.estimated_aud_price || "0",
    discount_received: "0",
    store: item.store || "",
    purchase_date: new Date().toISOString().slice(0, 10),
    receipt: null,
    purchaser: user?.name || "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = (Math.max(Number(form.actual_aud_price) - Number(form.discount_received), 0) * Number(form.quantity)) || 0;

  const save = async () => {
    if (Number(form.quantity) <= 0) return toast.error("Jumlah harus lebih dari 0");
    setSaving(true);
    try {
      await api.post("/purchases", {
        trip_id: item.trip_id,
        product_id: item.product_id,
        product_name: item.name,
        store: form.store,
        purchase_date: form.purchase_date,
        actual_aud_price: String(form.actual_aud_price),
        discount_received: String(form.discount_received),
        quantity: Number(form.quantity),
        receipt: form.receipt,
        purchaser: form.purchaser,
        notes: form.notes,
      });
      toast.success("Pembelian dicatat");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Catat Pembelian · {item.name}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Jumlah Dibeli" required>
            <div className="flex items-center gap-2">
              <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => set("quantity", Math.max(Number(form.quantity) - 1, 1))} data-testid="sl-qty-minus"><Minus className="h-4 w-4" /></Button>
              <Input type="number" className="text-center" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="sl-purchase-qty" />
              <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => set("quantity", Number(form.quantity) + 1)} data-testid="sl-qty-plus"><Plus className="h-4 w-4" /></Button>
            </div>
          </Field>
          <Field label="Harga per Unit (AUD)" required><Input type="number" step="0.01" value={form.actual_aud_price} onChange={(e) => set("actual_aud_price", e.target.value)} data-testid="sl-purchase-price" /></Field>
          <Field label="Diskon per Unit (AUD)"><Input type="number" step="0.01" value={form.discount_received} onChange={(e) => set("discount_received", e.target.value)} /></Field>
          <Field label="Total Pembelian (AUD)"><Input value={formatAUD(total)} readOnly className="bg-secondary/40" data-testid="sl-purchase-total" /></Field>
          <Field label="Toko"><Input value={form.store} onChange={(e) => set("store", e.target.value)} data-testid="sl-purchase-store" /></Field>
          <Field label="Tanggal"><Input type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} /></Field>
          <Field label="Staff Pembeli"><Input value={form.purchaser} onChange={(e) => set("purchaser", e.target.value)} /></Field>
          <div className="sm:col-span-2"><Field label="Struk / Bukti"><ImageUpload value={form.receipt} onChange={(b) => set("receipt", b)} testId="sl-purchase-receipt" /></Field></div>
          <div className="sm:col-span-2"><Field label="Catatan"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="sl-purchase-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AllocationDialog({ item, isAdmin, defaultMethod, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_ordered: 0, total_purchased: 0, total_allocated: 0 });
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState(defaultMethod || "oldest_first");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/shopping-list/${item.product_id}/customers`, { params: { trip_id: item.trip_id } });
      setRows(r.data.rows.map((x) => ({ ...x })));
      setTotals({ total_ordered: r.data.total_ordered, total_purchased: r.data.total_purchased, total_allocated: r.data.total_allocated });
    } catch { toast.error("Gagal memuat customer"); }
    finally { setLoading(false); }
  }, [item.product_id, item.trip_id]);
  useEffect(() => { load(); }, [load]);

  const totalAlloc = rows.reduce((a, r) => a + (Number(r.allocated_quantity) || 0), 0);
  const setAlloc = (idx, v) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, allocated_quantity: v } : r));

  const save = async () => {
    if (totalAlloc > totals.total_purchased) return toast.error(`Total alokasi (${totalAlloc}) melebihi jumlah dibeli (${totals.total_purchased})`);
    setBusy(true);
    try {
      await api.post(`/shopping-list/${item.product_id}/allocate`, {
        allocations: rows.map((r) => ({ order_id: r.order_id, item_index: r.item_index, allocated_quantity: Number(r.allocated_quantity) || 0 })),
      });
      toast.success("Alokasi disimpan");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Gagal menyimpan alokasi"); }
    finally { setBusy(false); }
  };

  const autoAllocate = async () => {
    setBusy(true);
    try {
      await api.post(`/shopping-list/auto-allocate`, { trip_id: item.trip_id, method, product_id: item.product_id });
      toast.success("Alokasi otomatis diterapkan");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal alokasi otomatis"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Alokasi Customer · {item.name}</DialogTitle></DialogHeader>
        {loading ? <Loading /> : (
          <>
            <div className="grid grid-cols-3 gap-2 text-center mb-3">
              <div className="rounded-lg bg-secondary/40 p-2"><p className="text-[11px] text-muted-foreground">Dipesan</p><p className="font-semibold">{totals.total_ordered}</p></div>
              <div className="rounded-lg bg-secondary/40 p-2"><p className="text-[11px] text-muted-foreground">Dibeli</p><p className="font-semibold">{totals.total_purchased}</p></div>
              <div className="rounded-lg bg-secondary/40 p-2"><p className="text-[11px] text-muted-foreground">Teralokasi</p><p className={`font-semibold ${totalAlloc > totals.total_purchased ? "text-[hsl(var(--danger))]" : ""}`}>{totalAlloc}</p></div>
            </div>

            {isAdmin && (
              <div className="flex items-end gap-2 mb-3 no-print">
                <div className="flex-1">
                  <Field label="Metode Alokasi Otomatis">
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger data-testid="sl-alloc-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{ALLOC_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Button variant="outline" onClick={autoAllocate} disabled={busy || method === "manual"} data-testid="sl-alloc-auto">Terapkan Otomatis</Button>
              </div>
            )}

            {rows.length === 0 ? <EmptyState title="Belum ada customer" /> : (
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                {rows.map((r, idx) => (
                  <div key={`${r.order_id}-${r.item_index}`} className="flex items-center justify-between gap-2 p-3 border-b border-border last:border-0" data-testid="sl-alloc-row">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{r.order_number} · {formatDate(r.order_date)}</p>
                      <div className="flex gap-1 mt-1"><StatusBadge status={r.order_status} /><StatusBadge status={r.payment_status} /></div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-center"><p className="text-[10px] text-muted-foreground">Pesan</p><p className="font-semibold">{r.ordered_quantity}</p></div>
                      <div className="w-24">
                        <p className="text-[10px] text-muted-foreground mb-0.5">Alokasi</p>
                        <Input type="number" min="0" value={r.allocated_quantity} disabled={!isAdmin}
                          onChange={(e) => setAlloc(idx, e.target.value)} data-testid="sl-alloc-input" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Tutup</Button>
          {isAdmin && <Button onClick={save} disabled={busy || loading} data-testid="sl-alloc-save">Simpan Alokasi</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusDialog({ item, onClose, onSaved }) {
  const [override, setOverride] = useState(item.status_override || "none");
  const [notes, setNotes] = useState(item.notes || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/shopping-list/${item.product_id}/purchase-status`, {
        status_override: override === "none" ? "" : override,
        notes,
      });
      toast.success("Status diperbarui");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Status & Catatan · {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Status Override" hint="Kosongkan untuk status otomatis berdasarkan pembelian">
            <Select value={override} onValueChange={setOverride}>
              <SelectTrigger data-testid="sl-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Otomatis (berdasarkan pembelian)</SelectItem>
                {OVERRIDE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Catatan"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sl-status-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} data-testid="sl-status-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
